import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';

import { Origin } from '@classes/origin/origin';
import { OriginService } from '@services/origin.service';
import { UiService } from '@services/ui.service';
import { OriginConfig } from '@classes/origin/origin-config';
import { UserParameter } from '@classes/user-parameter';

@Component({
    selector: 'app-origin-selector',
    templateUrl: './origin-selector.component.html',
    styleUrls: ['./origin-selector.component.scss']
})
export class OriginSelectorComponent implements OnInit, OnDestroy {
    private originsListSub: Subscription;
    private loadingStateSub: Subscription;

    userOrigin: Origin;
    originForm: FormGroup;

    options: Origin[];
    filteredOptions: Observable<Origin[]>;

    isLoading = true;

    constructor(
        private originSrv: OriginService,
        private route: ActivatedRoute,
        private uiSrv: UiService
    ) { }

    ngOnInit() {
        this.originSrv.getList().subscribe();
        this.initSubscriptions();
    }

    onSubmit(): void {
        if (this.originForm.value.origin != null && typeof this.originForm.value.origin !== 'string') {
            const newConfig = new OriginConfig({ code: this.originForm.value.origin.code });
            this.originSrv.set(newConfig).subscribe(() => { this.initForm(); });

        } else {
            this.uiSrv.showSnackbar("main.dialogs.badOrigin.fullLabel", true);
        }
    }

    displayFn(origin?: Origin): string | undefined {
        return origin ? origin.text.fullLabel : undefined;
    }

    private initSubscriptions(): void {
        this.route.data
        .subscribe((data: { userParameters:  UserParameter}) => {
            if(data && data.userParameters) {
                this.userOrigin = data.userParameters.origin;
            }
        });

        this.originsListSub = this.originSrv.originsListChanged
        .subscribe(originsList => {
            if(originsList) {
                this.options = originsList;

                this.initForm();
            }
        });

        this.loadingStateSub = this.uiSrv.loadingStateChanged
        .subscribe(loadingState => {
            this.isLoading = loadingState;
        });
    }

    private initForm(): void {
        this.originForm = new FormGroup({
            origin: new FormControl({
                value: this.userOrigin,
                disabled: this.options.length <= 1
            }, {
                validators: Validators.required
            })
        });

        this.setFilteredOptions();
    }

    private setFilteredOptions(): void {
        this.filteredOptions = this.originForm.controls.origin.valueChanges
            .pipe(
                startWith<string | Origin>(''),
                map(value => {
                    if (typeof value === 'string') {
                        return value;
                    } else {
                        return value.text.fullLabel;
                    }
                }),
                map(name => name ? this._filter(name) : this.options.slice())
            );
    }

    private _filter(value: string): Origin[] {
        const filterValue = value.toLowerCase();
        return this.options.filter(option => option.text.fullLabel.toLowerCase().includes(filterValue));
    }

    ngOnDestroy() {
        if(this.originsListSub) {
            this.originsListSub.unsubscribe();
        }

        if(this.loadingStateSub) {
            this.loadingStateSub.unsubscribe();
        }
    }
}
